import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authFetch } from '@/lib/auth-fetch'

export interface PushNotification {
  id: string
  title: string
  body: string
  url: string
  symbol: string
  direction?: string
  confidence?: string    // "HIGH" | "LOW" | "NORMAL" | "EARLY"
  tag?: string           // "CVD" | "LIQ" | "WATCH" | "FEED" | "HIGH" | "NORMAL" | "LOW" | "UNVERIFIED"
  ai_approved?: boolean | null
  ai_reasoning?: string
  receivedAt: number
  read: boolean
}

interface NotificationState {
  notifications: PushNotification[]
  unreadCount: number
  showSignalModal: boolean
  serverLoaded: boolean

  add: (n: Omit<PushNotification, 'id' | 'receivedAt' | 'read'>) => void
  markRead: (id: string) => void
  markAllRead: () => void
  clear: () => void
  setShowSignalModal: (open: boolean) => void
  loadFromServer: () => Promise<void>
}

export const useNotificationStore = create<NotificationState>()(
  persist(
    (set, get) => ({
      notifications: [],
      unreadCount: 0,
      showSignalModal: false,
      serverLoaded: false,
      setShowSignalModal: (open) => set({ showSignalModal: open }),

      add: (n) => {
        const next: PushNotification = {
          ...n,
          id: crypto.randomUUID(),
          receivedAt: Date.now(),
          read: false,
        }
        const updated = [next, ...get().notifications].slice(0, 20)
        set({ notifications: updated, unreadCount: updated.filter((x) => !x.read).length })
      },

      markRead: (id) => {
        const updated = get().notifications.map((n) => (n.id === id ? { ...n, read: true } : n))
        set({ notifications: updated, unreadCount: updated.filter((x) => !x.read).length })
      },

      markAllRead: () => {
        const updated = get().notifications.map((n) => ({ ...n, read: true }))
        set({ notifications: updated, unreadCount: 0 })
      },

      clear: () => set({ notifications: [], unreadCount: 0 }),

      loadFromServer: async () => {
        if (get().serverLoaded) return
        try {
          const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || 'http://localhost:8000'
          const res = await authFetch(`${backendUrl}/api/notifications/history?limit=30`)
          if (!res.ok) return
          const data = await res.json()
          if (!Array.isArray(data) || data.length === 0) {
            set({ serverLoaded: true })
            return
          }
          // Merge server notifications with local, dedup by symbol+direction+receivedAt proximity
          const local = get().notifications
          const localKeys = new Set(local.map((n) => `${n.symbol}|${n.direction}|${Math.floor(n.receivedAt / 60000)}`))
          const newFromServer: PushNotification[] = []
          for (const item of data) {
            const key = `${item.symbol || ''}|${item.direction || ''}|${Math.floor((item.receivedAt || 0) / 60000)}`
            if (!localKeys.has(key)) {
              newFromServer.push({
                id: item.id || crypto.randomUUID(),
                title: item.title || '',
                body: item.body || '',
                url: item.url || '/terminal',
                symbol: item.symbol || '',
                direction: item.direction,
                confidence: item.confidence,
                tag: item.tag,
                ai_approved: item.ai_approved,
                ai_reasoning: item.ai_reasoning,
                receivedAt: item.receivedAt || Date.now(),
                read: item.read ?? true,
              })
            }
          }
          if (newFromServer.length > 0) {
            const merged = [...local, ...newFromServer]
              .sort((a, b) => b.receivedAt - a.receivedAt)
              .slice(0, 30)
            set({
              notifications: merged,
              unreadCount: merged.filter((x) => !x.read).length,
              serverLoaded: true,
            })
          } else {
            set({ serverLoaded: true })
          }
        } catch {
          // Non-fatal — server history is enhancement
          set({ serverLoaded: true })
        }
      },
    }),
    {
      name: 'algoglide-notifications',
      partialize: (state) => ({
        notifications: state.notifications,
        unreadCount: state.unreadCount,
      }),
    }
  )
)
